import { computed, nextTick, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue'

export const TABLE_SCROLL_BREAKPOINT = 1280
export const TABLE_SCROLL_Y = {
  small: 10,
  twoCards: 38,
  full: {
    percent: 100,
    offset: 320,
  },
}

function getScrollY(value, viewportHeight) {
  const fallback = TABLE_SCROLL_Y.small
  const scrollY = value ?? fallback

  if (typeof scrollY === 'number') {
    return Math.round(viewportHeight * scrollY / 100)
  }

  if (typeof scrollY === 'object') {
    return Math.max(
        0,
        Math.round(viewportHeight * (scrollY.percent ?? 100) / 100 - (scrollY.offset ?? 0))
    )
  }

  return getScrollY(fallback, viewportHeight)
}

export function useTableScroll({
  scrollX,
  dataSource = null,
  breakpoint = TABLE_SCROLL_BREAKPOINT,
  scrollY = TABLE_SCROLL_Y.small,
} = {}) {
  const tableRef = ref(null)
  const needsYScroll = ref(false)
  const viewportWidth = ref(window.innerWidth)
  const viewportHeight = ref(window.innerHeight)

  const maxY = computed(() =>
      viewportWidth.value < breakpoint
          ? undefined
          : getScrollY(unref(scrollY), viewportHeight.value)
  )

  const tableScroll = computed(() => ({
    x: unref(scrollX),
    y: needsYScroll.value ? maxY.value : undefined,
  }))

  const updateTableYScroll = async () => {
    await nextTick()

    if (viewportWidth.value < breakpoint) {
      needsYScroll.value = false
      return
    }

    const tableEl = tableRef.value?.$el ?? tableRef.value
    if (!tableEl) return

    const tbody = tableEl.querySelector('.ant-table-tbody')
    if (!tbody) return

    needsYScroll.value = Boolean(maxY.value && tbody.scrollHeight > maxY.value)
  }

  const handleResize = () => {
    viewportWidth.value = window.innerWidth
    viewportHeight.value = window.innerHeight
    updateTableYScroll()
  }

  onMounted(() => {
    window.addEventListener('resize', handleResize)
    updateTableYScroll()
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
  })

  if (dataSource) {
    watch(
      () => unref(dataSource),
      updateTableYScroll,
      { deep: true, flush: 'post' }
    )
  }

  return {
    tableRef,
    tableScroll,
    updateTableYScroll,
  }
}
